import React, { useState, useEffect } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Checkin = () => {
  const { bookingId } = useParams();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [guestAssignments, setGuestAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookingDetails = async () => {
      const hostData = getItemFromLocalStorage('hostProfile');
      let formData = new FormData();
      formData.append('token', hostData.host_access_token);
      formData.append('confirmation_code', bookingId);

      setLoading(true);
      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          url: `${params.baseURL}${params.endpoints.getBookings}`,
          data: formData,
        });

        if (response.data.status === 'found') {
          const bookings = response.data.bookings;
          setBookingDetails(bookings);
          initializeGuestAssignments(bookings.selectedCriterias.adults, bookings.guest_info);
          console.log(bookings);
        } else {
          setError('Booking not found.');
        }
      } catch (err) {
        setError('An error occurred while fetching booking details.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  const initializeGuestAssignments = (noOfGuests, guestInfo) => {
    const assignments = [];
  
    if (guestInfo) {
      const mainGuestName = `${guestInfo.title || ''} ${guestInfo.first_name || ''} ${guestInfo.last_name || ''}`.trim();
      assignments.push({
        guest_name: mainGuestName,
        guest_identity_proof: null,
      });
  
      if (Array.isArray(guestInfo.guest_list) && guestInfo.guest_list.length > 0) {
        guestInfo.guest_list.forEach((g) => {
          assignments.push({
            guest_name: g.name || '',
            guest_identity_proof: null,
          });
        });
      }
  
      while (assignments.length < noOfGuests) {
        assignments.push({
          guest_name: '',
          guest_identity_proof: null,
        });
      }
    } else {
      for (let i = 0; i < noOfGuests; i++) {
        assignments.push({
          guest_name: '',
          guest_identity_proof: null,
        });
      }
    }
  
    setGuestAssignments(assignments);
  };  

  const handleGuestChange = (guestIndex, field, value) => {
    const updatedGuests = [...guestAssignments];
    updatedGuests[guestIndex][field] = value;
    setGuestAssignments(updatedGuests);
  };

  const handleFileUpload = (e, guestIndex) => {
    const file = e.target.files[0];
    if (file) {
      const updatedGuestAssignments = [...guestAssignments];
      updatedGuestAssignments[guestIndex].guest_identity_proof = file;
      setGuestAssignments(updatedGuestAssignments);
    }
  };

  const handleSubmit = async () => {
    const isValid = validateInputs();
    if (!isValid) return;

    setLoading(true);
    setError(null);

    let formData = new FormData();
    formData.append('stay_booking_id', bookingDetails.stay_booking_id);
    guestAssignments.forEach((guest, guestIndex) => {
      formData.append(`guest_assignments[${guestIndex}][guest_name]`, guest.guest_name);
  
      if (guest.guest_identity_proof) {
        formData.append(`guest_assignments[${guestIndex}][guest_identity_proof]`, guest.guest_identity_proof);
      }
    });
    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: `${params.baseURL}${params.endpoints.saveCheckin}`,
        data: formData,
      });
      if (response.data.status === 'success') {
        toast.success('Check-in successful.');
        navigate('/home');
      } else {
        setError('An error occurred while saving check-in details.');
      }
    } catch (err) {
      setError('Check-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateInputs = () => {
    for (let guestIndex = 0; guestIndex < guestAssignments.length; guestIndex++) {
      const guest = guestAssignments[guestIndex];
  
      if (!guest.guest_name.trim()) {
        setError(`Guest ${guestIndex + 1} name is missing.`);
        return false;
      }
  
      if (!guest.guest_identity_proof) {
        setError(`Guest ${guestIndex + 1} must upload an identity proof.`);
        return false;
      }
    }
  
    setError(null);
    return true;
  };  

  return (
    <div>
      <Helmet>
        <title>Manage Check-in</title>
        <meta name="description" content="" />
      </Helmet>

      <div className="manage-checkin" style={{width:'800px', margin:'auto'}}>
        <div className="hms-header mb-0">
          <div className='iwtb mb-0'>
            <img src={'/assets/img/icons/pointer_icon.svg'} alt="Profile" className="bd-icon" />
            <h1 className="ws-title m-0">Manage Check-in</h1>
          </div>
        </div>

        {loading && <p>Loading...</p>}

        {bookingDetails && (
          <div className="booking-details">
            <div className="hms-header">
              <div className="iwtb">
                <img src={'/assets/img/icons/pointer_icon.svg'} alt="Profile" className="bd-icon" />
                <p className="bd-title">Booking Details</p>
              </div>
              <div className="bn-row">
                <div className="bn-col">
                  <div className='bnswit'>
                    <img src="/assets/img/icons/pointer_icon.svg" alt="ID" className="bd-icon" />
                    <div className="bdbib">
                      <p>Booking ID: {bookingDetails.confirmation_code}</p>
                    </div>
                  </div>
                </div>
                <div className="bn-col">
                  <div className='bnswit'>
                    <img src="/assets/img/icons/pointer_icon.svg" alt="Guests" className="bd-icon" />
                    <div className="bdbib">
                      <p>Number of Guests: {bookingDetails.selectedCriterias.adults}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/*
            {bookingDetails.customer_documents && (
              <div className="row mb-3" style={{gap : 0}}>
                {bookingDetails.customer_documents.front && (
                  <div className='col-md-6'>
                    <a
                      href={bookingDetails.customer_documents.front}
                      target="_blank"
                      className="btn btn-black w-100"
                      rel="noopener noreferrer"
                      style={{alignContent: 'center' }}
                    >
                      View Front Document
                    </a>
                  </div>
                )}
                {bookingDetails.customer_documents.back && (
                  <div className='col-md-6'>
                    <a
                      href={bookingDetails.customer_documents.back}
                      target="_blank"
                      className="btn btn-black w-100"
                      rel="noopener noreferrer"
                      style={{alignContent: 'center' }}
                    >
                      View Back Document
                    </a>
                  </div>
                )}
              </div>
            )}
            */}
            <div className="guest-assignments">
              {guestAssignments.map((guest, guestIndex) => (
                <div key={guestIndex}  className="guest-details-box">
                  <div className="form-group gdsbx">
                    <label>
                      Guest {guestIndex + 1} Name:
                      <div className='guest-name-box'>
                        <img src={'/assets/img/icons/pointer_icon.svg'} alt="Profile" className="bd-icon" />
                        <input
                          type="text"
                          className="form-control"
                          value={guest.guest_name}
                          onChange={(e) => handleGuestChange(guestIndex, 'guest_name', e.target.value)}
                        />
                      </div>
                    </label>
                  </div>
                  <div className="form-group gdsbx">
                    <label>
                      Identity Proof:
                      <div className='guest-name-box'>
                        <img src={'/assets/img/icons/pointer_icon.svg'} alt="Profile" className="bd-icon" />
                        {guestIndex === 0 && bookingDetails.customer_documents?.front && bookingDetails.customer_documents?.back ? (
                          <button
                            type="button"
                            className="btn btn-black"
                            style={{width:'auto'}}
                            onClick={() => window.open(`/view-documents/${bookingDetails.confirmation_code}`, '_blank')}
                          >
                            View Documents
                          </button>
                        ) : (
                          <input
                            type="file"
                            className="form-control"
                            onChange={(e) => handleFileUpload(e, guestIndex)}
                          />
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {error && <p className="error" style={{color:'red'}}>{error}</p>}

            <button onClick={handleSubmit} className="btn btn-black hlbtn" disabled={loading}>Submit</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkin;
